import Big from 'big.js';
import axios from '../axios';
import Batch from '../batch';
import { GetReadableSize } from '../lineitemtypesizes';
import * as models from '../models';

export interface LineItem {
    id: string;
    name: string;
    pluralName: string;
    types: Type[];
    children: Child[];
    quoteForm: boolean;
    builder: boolean;

    realName: string;
    realPluralName: string;
}

export interface Child {
    id: string;
    name: string;
    pluralName: string;
    options: Option[];
    price: Big;
    quoteForm: boolean;
    builder: boolean;

    realName: string;
    realPluralName: string;
}

export interface Option {
    id: string;
    name: string;
    price: Big;
    pluralName: string;
    quoteForm: boolean;
    builder: boolean;

    realName: string;
    realPluralName: string;
}

export interface Type {
    id: string;
    name: string;
    sizes: Size[];
    pluralName: string;
    quoteForm: boolean;
    builder: boolean;

    realName: string;
    realPluralName: string;
}

export interface Size {
    id: string;
    width: number;
    prices: Price[];
    type: string;
    height: number;
    name: string;
    area: number;
    quoteForm: boolean;
    builder: boolean;

    realName: string;
}

export interface Price {
    lineItem: string;
    type: string;
    size: string;
    quantity: number;
    price: Big;
}

declare interface lineItemsResp {
    lineItems: {
        id: string;
        /** name */
        n: string;
        /** plural name */
        pn?: string;
        /** real name */
        rn?: string;
        /** real plural name */
        rp?: string;
        /** quote form */
        qf?: boolean;
        /** builder */
        b?: boolean;
        /** types */
        t?: {
            id: string;
            /** name */
            n: string;
            /** plural name */
            pn?: string;
            /** real name */
            rn?: string;
            /** real plural name */
            rp?: string;
            /** quote form */
            qf?: boolean;
            /** builder */
            b?: boolean;
            /** sizes */
            s?: {
                id: string;
                /** width */
                w: number;
                /** type */
                t?: string;
                /** height */
                h?: number;
                /** name */
                n?: string;
                /** real name */
                rn?: string;
                /** quote form */
                qf?: boolean;
                /** builder */
                b?: boolean;
                /** prices */
                p?: {
                    /** quantity */
                    q: number;
                    /** price */
                    p: number;
                }[];
            }[];
        }[];
        /** children */
        c?: {
            id: string;
            /** name */
            n: string;
            /** plural name */
            pn?: string;
            /** price */
            p?: number;
            /** real name */
            rn?: string;
            /** real plural name */
            rp?: string;
            /** quote form */
            qf?: boolean;
            /** builder */
            b?: boolean;
            /** options */
            o?: {
                id: string;
                /** name */
                n: string;
                /** price */
                p?: number;
                /** plural name */
                pn?: string;
                /** real name */
                rn?: string;
                /** real plural name */
                rp?: string;
                /** quote form */
                qf?: boolean;
                /** builder */
                b?: boolean;
            }[];
        }[];
    }[];
}

let lineItems: Promise<LineItem[]>;

export async function GetLineItems(b?: Batch): Promise<LineItem[]> {
    if (typeof lineItems === 'undefined') {
        lineItems = (b ? b.get<lineItemsResp>('/line-items') : axios.get<lineItemsResp>('/line-items')).then(resp => {
            const lineItems = [];

            for (const l of resp.data.lineItems) {
                const newLineItem: LineItem = {
                    id: l.id,
                    name: l.n,
                    pluralName: l.pn || l.n,
                    realName: l.rn || l.n,
                    realPluralName: l.rp || l.rn || l.pn || l.n,
                    quoteForm: l.qf || false,
                    builder: l.b || false,
                    types: [],
                    children: [],
                };

                if (l.t) {
                    for (const t of l.t) {
                        const newType: Type = {
                            id: t.id,
                            name: t.n,
                            pluralName: t.pn || t.n,
                            realName: t.rn || t.n,
                            realPluralName: t.rp || t.rn || t.pn || t.n,
                            quoteForm: t.qf || false,
                            builder: t.b || false,
                            sizes: [],
                        };

                        if (t.s) {
                            for (const s of t.s) {
                                const readableName = GetReadableSize(s.w, s.h || s.w, s.t || '');
                                const newSize: Size = {
                                    id: s.id,
                                    name: s.n || readableName,
                                    realName: s.rn || s.n || readableName,
                                    area: s.w * (s.h || s.w),
                                    width: s.w,
                                    height: s.h || s.w,
                                    type: s.t || '',
                                    quoteForm: s.qf || false,
                                    builder: s.b || false,
                                    prices: [],
                                };

                                if (s.p) {
                                    for (const p of s.p) {
                                        const newPrice: Price = {
                                            lineItem: newLineItem.pluralName || newLineItem.name,
                                            type: newType.pluralName || newType.name,
                                            size: newSize.realName,
                                            quantity: p.q,
                                            price: new Big(p.p),
                                        };

                                        newSize.prices.push(newPrice);
                                    }
                                }

                                newType.sizes.push(newSize);
                            }
                        }

                        newLineItem.types.push(newType);
                    }
                }

                if (l.c) {
                    for (const c of l.c) {
                        const newChild: Child = {
                            id: c.id,
                            name: c.n,
                            pluralName: c.pn || c.n,
                            realName: c.rn || c.n,
                            realPluralName: c.rp || c.rn || c.pn || c.n,
                            price: new Big(c.p || 0),
                            quoteForm: c.qf || false,
                            builder: c.b || false,
                            options: [],
                        };

                        if (c.o) {
                            for (const o of c.o) {
                                const newOption: Option = {
                                    id: o.id,
                                    name: o.n,
                                    pluralName: o.pn || o.n,
                                    realName: o.rn || o.n,
                                    realPluralName: o.rp || o.rn || o.pn || o.n,
                                    quoteForm: o.qf || false,
                                    builder: o.b || false,
                                    price: new Big(o.p || 0),
                                };

                                newChild.options.push(newOption);
                            }
                        }

                        newLineItem.children.push(newChild);
                    }
                }

                lineItems.push(newLineItem);
            }

            return lineItems;
        });
    }

    return lineItems;
}

export async function GetTypes(lineItem: string): Promise<Type[]> {
    for (const l of await GetLineItems()) {
        if (l.name.localeCompare(lineItem, undefined, { sensitivity: 'accent' }) === 0 ||
            l.pluralName.localeCompare(lineItem, undefined, { sensitivity: 'accent' }) === 0) {
            return l.types;
        }
    }

    return [];
}

export async function GetSizes(lineItem: string, type: string): Promise<Size[]> {
    for (const t of await GetTypes(lineItem)) {
        if (t.name.localeCompare(type, undefined, { sensitivity: 'accent' }) === 0 ||
            t.pluralName.localeCompare(type, undefined, { sensitivity: 'accent' }) === 0) {
            return t.sizes;
        }
    }

    return [];
}

export async function GetPrices(lineItem: string, type: string, width: number, height: number = width, sizeType: string = '', sizeName: string = GetReadableSize(width, height, sizeType)): Promise<Price[]> {
    for (const s of await GetSizes(lineItem, type)) {
        if (s.name.localeCompare(sizeName, undefined, { sensitivity: 'accent' }) === 0) {
            return s.prices;
        }
    }

    return [];
}

export async function GetPricesForAllSizes(lineItem: string, type: string): Promise<Price[]> {
    const prices = [];

    for (const s of await GetSizes(lineItem, type)) {
        prices.push(...s.prices);
    }

    return prices;
}

export async function GetPricesForAllTypes(lineItem: string): Promise<Price[]> {
    const prices = [];

    for (const t of await GetTypes(lineItem)) {
        for (const s of t.sizes) {
            prices.push(...s.prices);
        }
    }

    return prices;
}

export async function LowestPrice(lineItem: string, type?: string): Promise<Price | undefined> {
    let prices: Price[];
    if (!type) {
        prices = await GetPricesForAllTypes(lineItem);
    } else {
        prices = await GetPricesForAllSizes(lineItem, type);
    }

    let lowest: Price | undefined;
    for (const p of prices) {
        if (!lowest || p.price.lt(lowest.price)) {
            lowest = p;
        }
    }

    return lowest;
}

export async function LowestQuantity(lineItem: string, type?: string): Promise<Price | undefined> {
    let prices: Price[];
    if (!type) {
        prices = await GetPricesForAllTypes(lineItem);
    } else {
        prices = await GetPricesForAllSizes(lineItem, type);
    }

    let lowest: Price | undefined;
    for (const p of prices) {
        if (!lowest || p.quantity < lowest.quantity) {
            lowest = p;
        }
    }

    return lowest;
}

export async function GetChildren(lineItem: string): Promise<Child[]> {
    for (const l of await GetLineItems()) {
        if (l.name.localeCompare(lineItem, undefined, { sensitivity: 'accent' }) === 0 ||
            l.pluralName.localeCompare(lineItem, undefined, { sensitivity: 'accent' }) === 0) {
            return l.children;
        }
    }

    return [];
}

export async function GetChildPrice(lineItem: string, child: string): Promise<Big> {
    for (const c of await GetChildren(lineItem)) {
        if (c.name.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0 ||
            c.pluralName.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0) {
            return c.price;
        }
    }

    return new Big(0);
}

export async function GetOptions(lineItem: string, child: string): Promise<Option[]> {
    // console.log(child);
    for (const c of await GetChildren(lineItem)) {
        // console.log(c);
        // console.log(c.name.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0, c.pluralName.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0);
        if (c.name.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0 ||
            c.pluralName.localeCompare(child, undefined, { sensitivity: 'accent' }) === 0) {
            return c.options;
        }
    }

    return [];
}

export async function GetOptionPrice(lineItem: string, child: string, option: string): Promise<Big> {
    for (const o of await GetOptions(lineItem, child)) {
        if (o.name.localeCompare(option, undefined, { sensitivity: 'accent' }) === 0 ||
            o.pluralName.localeCompare(option, undefined, { sensitivity: 'accent' }) === 0) {
            return o.price;
        }
    }

    return new Big(0);
}

export async function GetPrice(lineItem: string, type: string, size: string, quantity: number): Promise<Big> {
    const s = await GetSizes(lineItem, type).then(s => s.find(s => s.name === size));
    if (!s) {
        return Big(0);
    }

    for (let i = s.prices.length - 1; i >= 0; i--) {
        const p = s.prices[i];
        if (p.quantity <= quantity) {
            return p.price;
        }
    }

    return new Big(0);
}

export async function GetPriceByArea(lineItem: string, type: string, width: number, height: number, quantity: number): Promise<Big> {
    const area = width * height;

    for (const s of await GetSizes(lineItem, type)) {
        if (s.area >= area) {
            for (let i = s.prices.length - 1; i >= 0; i--) {
                const p = s.prices[i];
                if (p.quantity <= quantity) {
                    return p.price;
                }
            }
            break;
        }
    }

    return new Big(0);
}

let maps: Promise<{
    lineItems: Map<string, LineItem>;
    types: Map<string, Type>;
    sizes: Map<string, Size>;
    childs: Map<string, Child>;
    options: Map<string, Option>;
}>;

async function getMaps(b?: Batch): typeof maps {
    if (typeof maps === 'undefined') {
        maps = new Promise(resolve => {
            const lineItems = new Map<string, LineItem>();
            const types = new Map<string, Type>();
            const sizes = new Map<string, Size>();
            const childs = new Map<string, Child>();
            const options = new Map<string, Option>();

            void GetLineItems(b).then(resp => {
                for (const lineItem of resp) {
                    lineItems.set(lineItem.id, lineItem);

                    for (const type of lineItem.types) {
                        types.set(type.id, type);

                        for (const size of type.sizes) {
                            sizes.set(size.id, size);
                        }
                    }

                    for (const child of lineItem.children) {
                        childs.set(child.id, child);

                        for (const option of child.options) {
                            options.set(option.id, option);
                        }
                    }
                }

                resolve({
                    lineItems: lineItems,
                    types: types,
                    sizes: sizes,
                    childs: childs,
                    options: options,
                });
            });
        });
    }

    return maps;
}

export async function LineItemMap(b?: Batch): Promise<Map<string, LineItem>> {
    return getMaps(b).then(m => m.lineItems);
}

export async function TypeMap(b?: Batch): Promise<Map<string, Type>> {
    return getMaps(b).then(m => m.types);
}

export async function SizeMap(b?: Batch): Promise<Map<string, Size>> {
    return getMaps(b).then(m => m.sizes);
}

export async function ChildMap(b?: Batch): Promise<Map<string, Child>> {
    return getMaps(b).then(m => m.childs);
}

export async function OptionMap(b?: Batch): Promise<Map<string, Option>> {
    return getMaps(b).then(m => m.options);
}

export async function LineItemByID(id: string, b?: Batch): Promise<LineItem | undefined> {
    return (await LineItemMap(b)).get(id);
}

export async function TypeByID(id: string, b?: Batch): Promise<Type | undefined> {
    return (await TypeMap(b)).get(id);
}

export async function SizeByID(id: string, b?: Batch): Promise<Size | undefined> {
    return (await SizeMap(b)).get(id);
}

export async function ChildByID(id: string, b?: Batch): Promise<Child | undefined> {
    return (await ChildMap(b)).get(id);
}

export async function OptionByID(id: string, b?: Batch): Promise<Option | undefined> {
    return (await OptionMap(b)).get(id);
}

export async function LineItemModelByID(id: string, b?: Batch): Promise<models.LineItem | undefined> {
    const lineItem = await LineItemByID(id, b);

    if (typeof lineItem === 'undefined') {
        return;
    }

    const lineItemModel: models.LineItem = {
        id: lineItem.id,
        name: lineItem.realName,
        pluralName: lineItem.realPluralName,
        networkLineItem: {
            name: lineItem.name,
            pluralName: lineItem.pluralName,
        },
    };
    return lineItemModel;
}

export async function LineItemTypeModelByID(id: string, b?: Batch): Promise<models.LineItemType | undefined> {
    const type = await TypeByID(id, b);

    if (typeof type === 'undefined') {
        return;
    }

    const typeModel: models.LineItemType = {
        id: type.id,
        name: type.realName,
        pluralName: type.realPluralName,
        networkLineItemType: {
            name: type.name,
            pluralName: type.pluralName,
        },
    };
    return typeModel;
}

export async function LineItemTypeSizeModelByID(id: string, b?: Batch): Promise<models.LineItemTypeSize | undefined> {
    const size = await SizeByID(id, b);

    if (typeof size === 'undefined') {
        return;
    }

    const sizeModel: models.LineItemTypeSize = {
        id: size.id,
        name: size.realName,
        width: String(size.width),
        height: String(size.height),

        networkLineItemTypeSize: {
            name: size.name,
        },
    };
    return sizeModel;
}

export async function ChildLineItemModelByID(id: string, b?: Batch): Promise<models.ChildLineItem | undefined> {
    const child = await ChildByID(id, b);

    if (typeof child === 'undefined') {
        return;
    }

    const childModel: models.ChildLineItem = {
        id: child.id,
        name: child.realName,
        pluralName: child.realPluralName,

        networkChildLineItem: {
            name: child.name,
            pluralName: child.pluralName,
        },
    };
    return childModel;
}

export async function ChildLineItemOptionModelByID(id: string, b?: Batch): Promise<models.ChildLineItemOption | undefined> {
    const option = await OptionByID(id, b);

    if (typeof option === 'undefined') {
        return;
    }

    const optionModel: models.ChildLineItemOption = {
        id: option.id,
        name: option.realName,
        pluralName: option.realPluralName,

        networkChildLineItemOption: {
            name: option.name,
            pluralName: option.pluralName,
        },
    };
    return optionModel;
}